import {
  Download,
  Pause,
  Play,
  Share2,
  Sparkles,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { buildBrandedShareMessage } from "../lib/share";
import type { GeneratedResult } from "../types";
import { VaffaMark } from "./VaffaMark";

const waveHeights = [
  12, 18, 29, 48, 62, 42, 22, 18, 35, 58, 68, 44, 20, 14, 26, 42, 55,
  37, 18, 28, 49, 65, 74, 52, 30, 20, 36, 58, 45, 25, 16, 12,
];

type Props = {
  result: GeneratedResult;
  justGenerated: boolean;
};

const brandedClosing = /(ma[.…,\s]*vaffangoo+glee+!?$)/i;

function PerformedScript({ script }: { script: string }) {
  const closing = script.match(brandedClosing);
  if (closing?.index == null) {
    return <p className="performed-script">{script}</p>;
  }

  return (
    <p className="performed-script">
      {script.slice(0, closing.index)}
      <strong>{closing[0]}</strong>
    </p>
  );
}

export function AudioConsole({ result, justGenerated }: Props) {
  const content = result;
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [shareMessage, setShareMessage] = useState("");
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioFileLoading, setAudioFileLoading] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
  }, [content.audioUrl]);

  useEffect(() => {
    let cancelled = false;
    setAudioFile(null);
    if (!content.audioUrl) {
      setAudioFileLoading(false);
      return;
    }

    setAudioFileLoading(true);
    fetch(content.audioUrl)
      .then((response) => {
        if (!response.ok) throw new Error("Audio scaduto o non disponibile.");
        return response.blob();
      })
      .then((blob) => {
        if (!cancelled) {
          setAudioFile(
            new File([blob], "vaffangoogle.mp3", { type: "audio/mpeg" }),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setAudioFile(null);
      })
      .finally(() => {
        if (!cancelled) setAudioFileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [content.audioUrl]);

  const togglePlay = async () => {
    setShareMessage("");
    const audio = audioRef.current;
    if (!content.audioUrl || !audio) {
      setShareMessage(
        "La voce non è disponibile in questo momento. Riprova più tardi.",
      );
      return;
    }
    if (audio.paused) {
      await audio.play();
    } else {
      audio.pause();
    }
  };

  const getShareMessage = () =>
    buildBrandedShareMessage({
      audioUrl: content.audioUrl,
      origin: window.location.origin,
      script: content.script,
    });

  const shareAudio = async () => {
    setShareMessage("");
    try {
      if (!content.audioUrl) {
        setShareMessage(
          "L’MP3 non è disponibile in questo momento. Riprova più tardi.",
        );
        return;
      }
      if (audioFile && navigator.canShare?.({ files: [audioFile] })) {
        await navigator.share({
          title: "Hai ricevuto un Vaffangoogle",
          text: getShareMessage(),
          files: [audioFile],
        });
        return;
      }
      if (navigator.share) {
        await navigator.share({
          title: "Hai ricevuto un Vaffangoogle",
          text: getShareMessage(),
        });
        setShareMessage(
          "Questo telefono non allega file dal browser: ho condiviso il link audio firmato Vaffangoogle.",
        );
        return;
      }
      await navigator.clipboard.writeText(getShareMessage());
      setShareMessage("Link audio e firma Vaffangoogle copiati.");
    } catch (reason) {
      if ((reason as DOMException).name !== "AbortError") {
        setShareMessage(
          reason instanceof Error ? reason.message : "Condivisione non riuscita.",
        );
      }
    }
  };

  return (
    <section
      id="risultato"
      className={`audio-console ${justGenerated ? "audio-console--fresh" : ""}`}
      aria-live="polite"
    >
      <div className="audio-console__title">
        <VaffaMark />
        <h2>{content.title}</h2>
      </div>

      <PerformedScript script={content.script} />

      {content.audioUrl ? (
        <>
          <div className="waveform" aria-hidden="true">
            {waveHeights.map((height, index) => (
              <span
                key={`${height}-${index}`}
                className={
                  progress > (index / waveHeights.length) * 100
                    ? "is-played"
                    : ""
                }
                style={{ height: `${height}%` }}
              />
            ))}
          </div>

          <audio
            ref={audioRef}
            src={content.audioUrl}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onTimeUpdate={(event) => {
              const audio = event.currentTarget;
              setProgress(
                audio.duration ? (audio.currentTime / audio.duration) * 100 : 0,
              );
            }}
            onEnded={() => {
              setPlaying(false);
              setProgress(100);
            }}
          />

          <div className="audio-actions">
            <button type="button" className="action-primary" onClick={togglePlay}>
              {playing ? (
                <Pause fill="currentColor" />
              ) : (
                <Play fill="currentColor" />
              )}
              {playing ? "Pausa" : "Ascolta"}
            </button>

            <button
              type="button"
              className="action-share-mp3"
              onClick={shareAudio}
              disabled={audioFileLoading}
            >
              <Share2 aria-hidden="true" />
              <span>
                <strong>
                  {audioFileLoading ? "Preparo l’MP3…" : "Condividi l’MP3"}
                </strong>
                <small>
                  {audioFileLoading
                    ? "sarà pronto tra un istante"
                    : "WhatsApp, Messaggi e altre app"}
                </small>
              </span>
            </button>

            <div className="share-brand-note">
              <VaffaMark />
              <span>
                <strong>Vaffangoogle incluso</strong>
                <small>nome, firma e sito viaggiano con il file</small>
              </span>
            </div>
          </div>

          <a
            className="download-link"
            href={content.audioUrl}
            download="vaffangoogle.mp3"
          >
            <Download size={16} /> Scarica MP3
          </a>

          <p className="ai-disclosure">
            <Sparkles aria-hidden="true" /> Voce interpretata dall’AI
          </p>
        </>
      ) : (
        <div className="audio-unavailable" role="status">
          <Sparkles aria-hidden="true" />
          <span>
            <strong>MP3 non disponibile</strong>
            <small>I controlli audio restano nascosti finché la voce non esiste.</small>
          </span>
        </div>
      )}

      {shareMessage ? <p className="share-message">{shareMessage}</p> : null}
    </section>
  );
}
